class CaneaCarousel extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    const track = this.querySelector('.canea-carousel__track');
    const buttons = [...this.querySelectorAll('[data-direction]')];
    if (!track) return;
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      buttons.forEach(b => { b.disabled = Number(b.dataset.direction) < 0 ? track.scrollLeft < 2 : track.scrollLeft >= max - 2; });
      const counter = this.querySelector('[data-current]');
      if (counter && track.firstElementChild) counter.textContent = String(Math.min(track.children.length, Math.round(track.scrollLeft / (track.firstElementChild.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).gap) || 0))) + 1));
    };
    buttons.forEach(b => b.addEventListener('click', () => {
      const step = track.firstElementChild.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).gap) || 0);
      track.scrollBy({left: Number(b.dataset.direction) * step, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    }));
    track.addEventListener('scroll', update, {passive:true});
    this.observer = new ResizeObserver(update);
    this.observer.observe(track);
    update();
  }
  disconnectedCallback() { this.observer?.disconnect(); this.ready = false; }
}
class CaneaQuantity extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    const input = this.querySelector('input');
    const minus = this.querySelector('[data-step="-1"]');
    const update = () => { minus.disabled = Number(input.value) <= Number(input.min); };
    this.querySelectorAll('[data-step]').forEach(button => button.addEventListener('click', () => {
      const next = (Number(input.value) || Number(input.min)) + Number(button.dataset.step);
      input.value = String(Math.max(Number(input.min), next));
      input.dispatchEvent(new Event('change', {bubbles:true}));
    }));
    input.addEventListener('input', update);
    input.addEventListener('change', update);
    update();
  }
}
class CaneaPartnerCode extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    const form = this.querySelector('form');
    const input = form.querySelector('input');
    const button = form.querySelector('button');
    const status = this.querySelector('[aria-live]');
    form.addEventListener('submit', async event => {
      event.preventDefault();
      const code = input.value.trim();
      if (!code) {status.textContent = this.dataset.empty; return;}
      button.disabled = true;
      status.textContent = '';
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(root + 'cart/update.js', {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({discount:code})});
        if (!response.ok) throw new Error('cart');
        const cart = await response.json();
        const codes = cart.discount_codes || [];
        const matched = codes.find(x => x.code?.toLowerCase() === code.toLowerCase());
        const discounts = [...(cart.cart_level_discount_applications || []), ...(cart.items || []).flatMap(x => (x.line_level_discount_allocations || []).map(a => a.discount_application))];
        const applied = matched?.applicable || discounts.some(x => x.title?.toLowerCase() === code.toLowerCase());
        status.textContent = applied ? this.dataset.applied : matched?.applicable === false && cart.item_count > 0 ? this.dataset.invalid : this.dataset.saved;
        if (applied && document.getElementById('CaneaCart')) window.location.reload();
      } catch (error) {status.textContent = this.dataset.error;}
      finally {button.disabled = false;}
    });
  }
}
class CaneaHeroMotion extends HTMLElement {
  connectedCallback() {
    if (this.controller || !this.classList.contains('canea-hero--animated')) return;
    this.controller = new AbortController();
    const signal = this.controller.signal;
    const toggle = this.querySelector('[data-logo-motion]');
    if (toggle) {
      toggle.hidden = false;
      this.classList.add('canea-hero--motion-ready');
      toggle.addEventListener('click', () => {
        const paused = this.classList.toggle('is-logo-paused');
        const label = paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel;
        toggle.setAttribute('aria-label', label);
        toggle.title = label;
      }, {signal});
      this.visibilityObserver = new IntersectionObserver(entries => {
        this.classList.toggle('is-hero-outside', !entries[0].isIntersecting || document.hidden);
      });
      this.visibilityObserver.observe(this);
      document.addEventListener('visibilitychange', () => {
        const bounds = this.getBoundingClientRect();
        this.classList.toggle('is-hero-outside', document.hidden || bounds.bottom <= 0 || bounds.top >= innerHeight);
      }, {signal});
    }
    const motion = matchMedia('(prefers-reduced-motion: no-preference) and (pointer: fine)');
    let pointerX = 0;
    let pointerY = 0;
    const render = () => {
      this.frame = 0;
      if (!motion.matches) {
        this.style.removeProperty('--hero-x');
        this.style.removeProperty('--hero-y');
        this.style.removeProperty('--hero-brand-y');
        return;
      }
      const bounds = this.getBoundingClientRect();
      if (bounds.bottom < 0 || bounds.top > innerHeight) return;
      const progress = Math.max(0, Math.min(1, -bounds.top / bounds.height));
      this.style.setProperty('--hero-x', `${pointerX * -8}px`);
      this.style.setProperty('--hero-y', `${pointerY * -5 + progress * bounds.height * .3}px`);
      this.style.setProperty('--hero-brand-y', `${progress * bounds.height * .12}px`);
    };
    const schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(render); };
    this.addEventListener('pointermove', event => {
      if (!motion.matches) return;
      const bounds = this.getBoundingClientRect();
      pointerX = (event.clientX - bounds.left) / bounds.width - .5;
      pointerY = (event.clientY - bounds.top) / bounds.height - .5;
      schedule();
    }, {signal, passive:true});
    this.addEventListener('pointerleave', () => { pointerX = 0; pointerY = 0; schedule(); }, {signal});
    window.addEventListener('scroll', schedule, {signal, passive:true});
    motion.addEventListener('change', schedule, {signal});
    schedule();
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.visibilityObserver?.disconnect();
    this.visibilityObserver = null;
    cancelAnimationFrame(this.frame);
  }
}
if (!customElements.get('canea-hero-motion')) customElements.define('canea-hero-motion', CaneaHeroMotion);
if (!customElements.get('canea-carousel')) customElements.define('canea-carousel', CaneaCarousel);
if (!customElements.get('canea-quantity')) customElements.define('canea-quantity', CaneaQuantity);
if (!customElements.get('canea-partner-code')) customElements.define('canea-partner-code', CaneaPartnerCode);
